import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ExpiryField, { expiryInputToIso } from '@/features/vault/ExpiryField';
import { credentialsApi, type CredentialType } from '@/api/credentials';
import { type CredentialTypeSchemaResponse } from '@/api/credentialSchemas';
import { errorMessage } from '@/api/client';
import { encryptCredential, type CredentialPayload } from '@/crypto/sodium';
import {
  CRED_TYPES,
  CredentialFormFields,
  partitionValues,
  useResetFieldsOnSchemaChange,
} from '@/features/vault/CredentialForm';

interface CreateFormProps {
  tenantId: string;
  ownerPublicKey: Uint8Array;
  schemasByType: Record<string, CredentialTypeSchemaResponse>;
  onDone: () => void;
}

export function CreateForm({ tenantId, ownerPublicKey, schemasByType, onDone }: CreateFormProps) {
  const { t } = useTranslation();
  const [type, setType] = useState<CredentialType>('password');
  const [title, setTitle] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [expires, setExpires] = useState('');
  const [error, setError] = useState<string | null>(null);

  const schema = schemasByType[type];
  useResetFieldsOnSchemaChange(schema, setValues, true);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!schema) throw new Error(t('vault_ui.list.schema_not_loaded'));
      const { plaintext, meta } = partitionValues(schema, values);
      const encrypted = await encryptCredential(
        plaintext as CredentialPayload,
        ownerPublicKey,
      );
      return credentialsApi.create(tenantId, {
        type,
        title,
        meta,
        expires_at: expiryInputToIso(expires) ?? undefined,
        ciphertext: encrypted.ciphertext,
        nonce: encrypted.nonce,
        dek_wrapped: encrypted.dek_wrapped,
      });
    },
    onSuccess: () => onDone(),
    onError: (err) => {
      setError(errorMessage(err, t('vault_ui.list.encrypt_error')));
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError(t('vault_ui.form.title_required'));
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="card mb-6">
      <h2 className="display-title-sm mb-4">{t('vault_ui.list.new_title')}</h2>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="vault-list-1" className="block text-xs font-medium text-[var(--text)] mb-1">{t('vault_ui.form.title_label')}</label>
            <input id="vault-list-1"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('vault_ui.list.title_placeholder')}
              required
              disabled={mutation.isPending}
            />
          </div>
          <div>
            <label htmlFor="vault-list-2" className="block text-xs font-medium text-[var(--text)] mb-1">{t('vault.col_type')}</label>
            <select id="vault-list-2"
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value as CredentialType)}
              disabled={mutation.isPending}
            >
              {CRED_TYPES.map((ct) => (
                <option key={ct.value} value={ct.value}>
                  {t(ct.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="pt-2">
          <CredentialFormFields
            schema={schema}
            values={values}
            onChange={setValues}
            disabled={mutation.isPending}
            expires={expires}
            onSuggestExpiry={setExpires}
          />
        </div>

        <ExpiryField value={expires} onChange={setExpires} disabled={mutation.isPending} />

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="submit" className="btn-primary" disabled={mutation.isPending || !schema}>
            {mutation.isPending ? t('vault_ui.list.encrypting') : t('common.save')}
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={onDone}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
